import { Controller } from "@hotwired/stimulus"
import { renderStreamMessage } from "@hotwired/turbo"

export default class extends Controller {
  static targets = ["hiddenPaymentMethod", "methodLink", "price", "summary"]
  static values = {
    checkoutBase: String,
    summariesUrl: String
  }

  connect() {
    // form_with(model: checkout) genera checkout[address_region] (select legacy o hidden de Places)
    this._regionField = this.element.querySelector('[name*="address_region"]')
    if (this._regionField) {
      this._onRegionChange = () => this._refreshSummariesForRegion()
      this._regionField.addEventListener("change", this._onRegionChange)
    }
    this._onFulfillmentChange = () => this._refreshSummariesForRegion()
    this.element.addEventListener("checkout-fulfillment-method:changed", this._onFulfillmentChange)
    this._loading = false
  }

  disconnect() {
    if (this._regionField && this._onRegionChange) {
      this._regionField.removeEventListener("change", this._onRegionChange)
    }
    if (this._onFulfillmentChange) {
      this.element.removeEventListener("checkout-fulfillment-method:changed", this._onFulfillmentChange)
    }
  }

  selectMethod(event) {
    event.preventDefault()
    if (this._loading) return

    const link = event.currentTarget
    let url = link.dataset.checkoutPaymentSummariesStreamUrl
    if (!url) return

    url = this._appendPreviewRegion(url)
    const slug = link.dataset.paymentMethodSlug
    const previousSlug = this._currentPaymentMethodSlug()
    this._updateMethodSelection(slug)
    this._fetchSummaries(url, slug, previousSlug)
  }

  _refreshSummariesForRegion() {
    if (this._loading) return

    const url = this._buildSummariesUrlFromBase()
    if (!url) return
    const slug = this._currentPaymentMethodSlug()
    this._fetchSummaries(url, slug)
  }

  _buildSummariesUrlFromBase() {
    if (!this.hasSummariesUrlValue) return null
    const url = new URL(this.summariesUrlValue, window.location.origin)
    const slug = this._currentPaymentMethodSlug()
    if (slug) url.searchParams.set("payment_method", slug)
    const region = this._regionField?.value
    if (region) url.searchParams.set("preview_region", region)
    this._applyPreviewFulfillmentParams(url)
    return url.toString()
  }

  _currentPaymentMethodSlug() {
    if (this.hasHiddenPaymentMethodTarget) {
      return this.hiddenPaymentMethodTarget.value
    }
    const current = this.methodLinkTargets?.find((el) => el.getAttribute("aria-current") === "true")
    return current?.dataset.paymentMethodSlug
  }

  _appendPreviewRegion(urlString) {
    if (!urlString) return urlString
    const u = new URL(urlString, window.location.origin)
    if (this._regionField?.value) {
      u.searchParams.set("preview_region", this._regionField.value)
    }
    this._applyPreviewFulfillmentParams(u)
    return u.toString()
  }

  _applyPreviewFulfillmentParams(url) {
    const selected = this.element.querySelector('input[name="checkout[fulfillment_method]"]:checked')
    if (selected?.value === "pickup") {
      url.searchParams.set("preview_fulfillment_method", "pickup")
    } else {
      url.searchParams.delete("preview_fulfillment_method")
    }
  }

  _fetchSummaries(url, slug, previousSlug = null) {
    if (this._loading) return

    this._loading = true
    this._showSkeletons()

    fetch(url, {
      method: "GET",
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        "X-Requested-With": "XMLHttpRequest"
      },
      credentials: "same-origin"
    })
      .then((response) => {
        if (!response.ok) throw new Error(response.statusText)
        return response.text()
      })
      .then((html) => {
        renderStreamMessage(html)
        if (slug) {
          this._updateMethodSelection(slug)
          this._replaceHistory(slug)
        }
      })
      .catch(() => {
        if (previousSlug) this._updateMethodSelection(previousSlug)
      })
      .finally(() => {
        this._loading = false
        this._hideSkeletons()
      })
  }

  _updateMethodSelection(slug) {
    if (!slug || !this.hasMethodLinkTarget) return

    if (this.hasHiddenPaymentMethodTarget) {
      this.hiddenPaymentMethodTarget.value = slug
    }

    this.methodLinkTargets.forEach((link) => {
      const selected = link.dataset.paymentMethodSlug === slug
      if (selected) {
        link.setAttribute("aria-current", "true")
      } else {
        link.removeAttribute("aria-current")
      }
      link.classList.toggle("border-button-secondary-bg", selected)
      link.classList.toggle("border-transparent", !selected)
      link.classList.toggle("hover:border-primary-light-color/25", !selected)
    })
  }

  _showSkeletons() {
    this.element.setAttribute("aria-busy", "true")
    if (this._regionField && this._regionField.tagName === "SELECT") this._regionField.disabled = true
    this.priceTargets.forEach((el) => el.classList.add("checkout-price-skeleton"))
    this.summaryTargets.forEach((el) => el.classList.add("checkout-summary-updating"))
  }

  _hideSkeletons() {
    this.element.removeAttribute("aria-busy")
    if (this._regionField && this._regionField.tagName === "SELECT") this._regionField.disabled = false
    this.priceTargets.forEach((el) => el.classList.remove("checkout-price-skeleton"))
    this.summaryTargets.forEach((el) => el.classList.remove("checkout-summary-updating"))
  }

  _replaceHistory(slug) {
    if (!this.hasCheckoutBaseValue) return
    const base = new URL(this.checkoutBaseValue, window.location.origin)
    if (slug) {
      base.searchParams.set("payment_method", slug)
    } else {
      base.searchParams.delete("payment_method")
    }
    window.history.replaceState({}, "", base.toString())
  }
}
